import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Typography, Box } from "@mui/material";
import React from "react";
import { editVariance } from "../../../../../services/varianceService/model/varianceRequest/VarianceRequestSlice";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { LegacySeverityItems, NewSeverityItems, SeverityItems } from "./SeverityItems";

function SeverityGroup(props: { canEdit: boolean, severityGroup: {value: string, displayName: string}, isNewIncident: boolean }) {
  const dispatch = useAppDispatch();
  const [severity, setSeverity] = React.useState({ value: "", description: "" });
  const incident = useAppSelector(
    (state) => state.varianceRequest.varianceRequest
  );

  const severityItemsToShow = React.useMemo(() => {
    const currentItem = incident.severity ? 
      SeverityItems.find(item => item.value === incident.severity) : null;
    
    if (currentItem) {
      // If this is a new incident but has a legacy severity value set
      const isLegacySeverity = LegacySeverityItems.some(item => item.value === incident.severity);
      
      // If we have a selected value and it belongs to the current group, prioritize showing that
      if (currentItem.group === props.severityGroup.value) {
        return props.isNewIncident && !isLegacySeverity ? 
          NewSeverityItems.filter(p => p.group === props.severityGroup.value) :
          LegacySeverityItems.filter(p => p.group === props.severityGroup.value);
      }
    }
    
    // No current selection or value from another group, show appropriate items based on incident type
    if (props.isNewIncident) {
      return NewSeverityItems.filter(p => p.group === props.severityGroup.value);
    } else {
      return LegacySeverityItems.filter(p => p.group === props.severityGroup.value);
    }
  }, [props.severityGroup.value, props.isNewIncident, incident.severity]);

  // If there are no items to show in this group, don't render anything
  if (severityItemsToShow.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <Box sx={{ margin: '20px', maxWidth: '600px', width: '100%' }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">{props.severityGroup.displayName}</FormLabel>
          {props.isNewIncident && (
            <Typography variant="caption" color="textSecondary">
              Using Safety Event Classification (SEC) codes
            </Typography>
          )}
          <RadioGroup
            value={incident.severity}
            onChange={(event) => {
              const value = event.target.value;
              const selectedSeverity = SeverityItems.find((item) => item.value === value);
              setSeverity(selectedSeverity || { value: "", description: "" });
              dispatch(editVariance({ name: "severity", value: value }));
            }}
          >
            {severityItemsToShow.map((item) => (
              <FormControlLabel
                disabled={!props.canEdit}
                key={item.value}
                value={item.value}
                control={<Radio />}
                label={props.isNewIncident ? 
                  `${item.value} - ${item.description}` : 
                  `${item.description}`
                }
                sx={{ marginY: 1 }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
    </React.Fragment>
  );
}

export {SeverityGroup};
